<template>
  <!-- Reusable, themeable info animation used by the Sistema PIE demo chatbot to
       explain each service. It is the same shell as PieIntroAnimation (auto-
       cycling stage + caption + dots + foot, hover pause, prefers-reduced-motion
       fallback, timer cleanup) but recolorable via the `color` prop and driven
       by a `features` list, each pointing at one of the generic animated scenes
       below. Styles are scoped and `svc-` prefixed; colors come from CSS vars
       derived from `color` so every scene re-themes per service. -->
  <div
    class="svc-info"
    :style="themeVars"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="svc-stage">
      <div class="svc-scene-wrap" aria-hidden="true">
        <transition name="svc-fade" mode="out-in">
          <!-- Estandarizado (Decreto 170) -->
          <div v-if="current.scene === 'standardized'" key="standardized" class="svc-scene">
            <div class="svc-seal"><span>D.170</span></div>
            <div class="svc-gauge">
              <b class="svc-needle"></b>
              <i class="svc-gauge-dot"></i>
            </div>
          </div>

          <!-- Formulario que el estudiante responde y se corrige solo -->
          <div v-else-if="current.scene === 'online-test'" key="online-test" class="svc-scene">
            <div class="svc-device">
              <span class="svc-dev-q"></span>
              <div
                class="svc-opt"
                v-for="o in 3"
                :key="o"
                :style="{ '--d': o * 0.4 + 's' }"
              >
                <span class="svc-opt-dot"></span>
                <i></i>
                <span class="svc-opt-check">✓</span>
              </div>
              <span class="svc-dev-grade">Corregido ✓</span>
            </div>
          </div>

          <!-- Informe generado -->
          <div v-else-if="current.scene === 'report'" key="report" class="svc-scene">
            <div class="svc-card svc-report">
              <div class="svc-rep-head">
                <i class="w50"></i><span class="svc-seal sm">✓</span>
              </div>
              <div class="svc-bars">
                <span style="--h: 50%; --d: 0s"></span>
                <span style="--h: 78%; --d: 0.15s"></span>
                <span style="--h: 62%; --d: 0.3s"></span>
                <span style="--h: 92%; --d: 0.45s"></span>
              </div>
            </div>
          </div>

          <!-- Diversificación (DUA) -->
          <div v-else-if="current.scene === 'dua'" key="dua" class="svc-scene">
            <div class="svc-dua">
              <span class="svc-kid k1">🧒</span>
              <span class="svc-kid k2">👧</span>
              <span class="svc-kid k3">👦</span>
              <div class="svc-card svc-dua-doc">
                <i class="w70"></i><i class="w50"></i><i class="w60"></i>
              </div>
            </div>
          </div>

          <!-- Listas en segundos -->
          <div v-else-if="current.scene === 'seconds'" key="seconds" class="svc-scene">
            <div class="svc-watch"><b class="svc-watch-hand"></b></div>
            <div class="svc-card svc-quickdoc">
              <i class="w70"></i><i class="w50"></i><i class="w60"></i>
              <span class="svc-ready">¡Listo!</span>
            </div>
          </div>

          <!-- Descarga al PC -->
          <div v-else-if="current.scene === 'download'" key="download" class="svc-scene">
            <div class="svc-dl">
              <div class="svc-card svc-dl-doc">
                <i class="w60"></i><i class="w70"></i>
                <span class="svc-dl-arrow">⬇</span>
              </div>
              <div class="svc-laptop"></div>
            </div>
          </div>

          <!-- Selección de varios OA y sus indicadores -->
          <div v-else-if="current.scene === 'oa-select'" key="oa-select" class="svc-scene">
            <div class="svc-card svc-oalist">
              <span class="svc-oa-head">OA · Lenguaje</span>
              <div
                class="svc-oa-row"
                v-for="(it, i) in oaItems"
                :key="i"
                :class="{ ind: it.lvl === 1 }"
                :style="{ '--d': i * 0.45 + 's' }"
              >
                <span class="svc-oa-box">✓</span>
                <i :class="it.lvl === 1 ? 'short' : 'w70'"></i>
              </div>
            </div>
          </div>

          <!-- Detalles adicionales de la planificación -->
          <div v-else-if="current.scene === 'details'" key="details" class="svc-scene">
            <div class="svc-card svc-details">
              <span class="svc-det-label">Detalles adicionales</span>
              <i class="svc-gline" style="--d: 0s"></i>
              <i class="svc-gline short" style="--d: 0.5s"></i>
              <div class="svc-chips">
                <span style="--d: 1s">tiempo</span>
                <span style="--d: 1.4s">recursos</span>
              </div>
            </div>
          </div>

          <!-- Genera una presentación PowerPoint -->
          <div v-else-if="current.scene === 'powerpoint'" key="powerpoint" class="svc-scene">
            <div class="svc-ppt">
              <div class="svc-card svc-ppt-doc">
                <i class="w70"></i><i class="w50"></i><i class="w60"></i>
              </div>
              <span class="svc-ppt-arrow">➜</span>
              <div class="svc-slides">
                <span class="svc-slide" style="--d: 0s"><b class="svc-ppt-tag">PPT</b></span>
                <span class="svc-slide" style="--d: 0.3s"></span>
                <span class="svc-slide" style="--d: 0.6s"></span>
              </div>
            </div>
          </div>

          <!-- Creación con IA -->
          <div v-else-if="current.scene === 'ai-generate'" key="ai-generate" class="svc-scene">
            <div class="svc-card svc-aidoc">
              <span class="svc-wand">✨</span>
              <i class="svc-gline" style="--d: 0s"></i>
              <i class="svc-gline" style="--d: 0.3s"></i>
              <i class="svc-gline short" style="--d: 0.6s"></i>
              <i class="svc-gline" style="--d: 0.9s"></i>
            </div>
          </div>

          <!-- Se corrige sola -->
          <div v-else-if="current.scene === 'self-correct'" key="self-correct" class="svc-scene">
            <div class="svc-card svc-correct">
              <div class="svc-crow" v-for="c in 3" :key="c" :style="{ '--d': c * 0.4 + 's' }">
                <i></i><span class="svc-tick">✓</span>
              </div>
              <div class="svc-score"><b>100%</b></div>
            </div>
          </div>

          <!-- Vitrina / catálogo -->
          <div v-else-if="current.scene === 'catalog'" key="catalog" class="svc-scene">
            <div class="svc-catalog">
              <div
                class="svc-mcard"
                v-for="m in 6"
                :key="m"
                :class="{ premium: m === 2 }"
                :style="{ '--d': m * 0.12 + 's' }"
              >
                <span v-if="m === 2" class="svc-star">★</span>
              </div>
            </div>
          </div>

          <!-- Checklist de documentos -->
          <div v-else-if="current.scene === 'checklist'" key="checklist" class="svc-scene">
            <div class="svc-card svc-checklist">
              <div
                class="svc-cli"
                v-for="(d, i) in docNames"
                :key="i"
                :style="{ '--d': i * 0.4 + 's' }"
              >
                <span class="svc-cbox">✓</span><span class="svc-cname">{{ d }}</span>
              </div>
            </div>
          </div>

          <!-- Transferencia entre documentos -->
          <div v-else-if="current.scene === 'transfer'" key="transfer" class="svc-scene">
            <div class="svc-transfer">
              <div class="svc-card svc-tdoc">
                <i class="svc-tfill" style="--d: 0s"></i>
                <i class="svc-tfill" style="--d: 0.3s"></i>
                <i class="svc-tfill short" style="--d: 0.6s"></i>
              </div>
              <div class="svc-tlink"><span class="svc-tpacket"></span></div>
              <div class="svc-card svc-tdoc">
                <i class="svc-tfill" style="--d: 1.2s"></i>
                <i class="svc-tfill short" style="--d: 1.5s"></i>
                <i class="svc-tfill" style="--d: 1.8s"></i>
              </div>
            </div>
          </div>

          <!-- Sugerencias e IA -->
          <div v-else-if="current.scene === 'suggestions'" key="suggestions" class="svc-scene">
            <div class="svc-card svc-sugdoc">
              <span class="svc-bulb">💡</span>
              <i class="w70"></i><i class="w50"></i>
              <div class="svc-chips">
                <span style="--d: 0s">sugerencia</span>
                <span style="--d: 0.4s">IA</span>
              </div>
            </div>
          </div>

          <!-- Ingreso de puntajes -->
          <div v-else-if="current.scene === 'scores-input'" key="scores-input" class="svc-scene">
            <div class="svc-card svc-scores">
              <div
                class="svc-srow"
                v-for="(n, i) in scoreNums"
                :key="i"
                :style="{ '--d': i * 0.5 + 's' }"
              >
                <i></i><b class="svc-num">{{ n }}</b>
              </div>
            </div>
          </div>

          <!-- Informe en Word -->
          <div v-else-if="current.scene === 'word'" key="word" class="svc-scene">
            <div class="svc-word">
              <div class="svc-wfile">
                <span class="svc-wmark">W</span>
                <i class="w70"></i><i class="short"></i><i class="w60"></i>
              </div>
              <span class="svc-wcheck">✓</span>
            </div>
          </div>
        </transition>
      </div>

      <div class="svc-caption-wrap">
        <transition name="svc-cap" mode="out-in">
          <div class="svc-caption" :key="active">
            <div class="svc-icon">{{ current.icon }}</div>
            <h4 v-html="current.title"></h4>
            <p v-html="current.text"></p>
          </div>
        </transition>
      </div>
    </div>

    <div class="svc-dots">
      <button
        v-for="(f, i) in features"
        :key="i"
        type="button"
        class="svc-dot"
        :class="{ active: i === active }"
        :aria-label="f.title"
        @click.stop="go(i)"
      ></button>
    </div>

    <div class="svc-foot" v-html="foot"></div>
  </div>
</template>

<script>
export default {
  name: "ServiceInfoAnimation",
  // condition/context/canWork are bound by ChatMessageComponent on the custom
  // component; absorb them so they don't leak onto the root element.
  props: {
    color: { type: String, required: true },
    features: { type: Array, required: true },
    foot: {
      type: String,
      required: false,
      default:
        'Y con el <strong>Sistema PIE</strong>, tu colegio lo usa <strong>sin límites</strong>.',
    },
    condition: { type: String, required: false, default: "" },
    context: { type: Object, required: false, default: () => ({}) },
    canWork: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      active: 0,
      timer: null,
      paused: false,
      reduce: false,
      INTERVAL: 3600,
      docNames: ["PACI", "PAI", "FU de reevaluación", "Estado de avance"],
      scoreNums: ["12", "08", "15"],
      // OA selection scene: lvl 0 = Objetivo de Aprendizaje, lvl 1 = indicador.
      oaItems: [
        { lvl: 0 },
        { lvl: 1 },
        { lvl: 0 },
        { lvl: 1 },
        { lvl: 0 },
      ],
    };
  },
  computed: {
    current() {
      return this.features[this.active] || {};
    },
    themeVars() {
      return {
        "--svc": this.color,
        "--svc-soft": this.rgba(0.1),
        "--svc-12": this.rgba(0.14),
        "--svc-line": this.rgba(0.26),
        "--svc-2": this.rgba(0.5),
        "--svc-glow": this.rgba(0.34),
      };
    },
  },
  mounted() {
    this.reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!this.reduce) this.startTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    rgba(a) {
      const h = (this.color || "#c6588d").replace("#", "");
      const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
      const r = parseInt(n.slice(0, 2), 16);
      const g = parseInt(n.slice(2, 4), 16);
      const b = parseInt(n.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    },
    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (!this.paused) this.next();
      }, this.INTERVAL);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    next() {
      this.active = (this.active + 1) % this.features.length;
    },
    go(i) {
      this.active = i;
      if (!this.reduce) this.startTimer();
    },
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    },
  },
};
</script>

<style scoped lang="scss">
$ink: #2f2330;

.svc-info {
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  padding: 18px 18px 14px;
  background: linear-gradient(160deg, #fff 0%, #fcfafe 100%);
  border: 1.5px solid var(--svc-line);
  font-family: "Ubuntu", Arial, sans-serif;
  overflow: hidden;
}

.svc-stage {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 210px;
}
.svc-scene-wrap {
  flex: 0 0 46%;
  max-width: 46%;
  height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-scene {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-caption-wrap {
  flex: 1 1 auto;
  min-width: 0;
}
.svc-caption {
  text-align: left;
  .svc-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--svc-soft);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 8px;
  }
  h4 {
    color: var(--svc);
    font-weight: 700;
    font-size: 1.05rem;
    margin: 0 0 6px;
    line-height: 1.2;
  }
  p {
    color: $ink;
    font-size: 0.92rem;
    margin: 0;
    line-height: 1.35;
  }
}

.svc-dots {
  display: flex;
  gap: 7px;
  justify-content: center;
  margin: 14px 0 6px;
}
.svc-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: var(--svc-line);
  cursor: pointer;
  transition: all 0.25s ease;
  &.active {
    background: var(--svc);
    width: 22px;
    border-radius: 6px;
  }
}
.svc-foot {
  text-align: center;
  font-size: 0.82rem;
  color: rgba(47, 35, 48, 0.7);
  ::v-deep strong {
    color: var(--svc);
  }
}

/* transitions */
.svc-fade-enter-active,
.svc-cap-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.svc-fade-leave-active,
.svc-cap-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.svc-fade-enter {
  opacity: 0;
  transform: scale(0.9);
}
.svc-fade-leave-to {
  opacity: 0;
  transform: scale(1.04);
}
.svc-cap-enter {
  opacity: 0;
  transform: translateY(10px);
}
.svc-cap-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* primitives */
.svc-card {
  background: #fff;
  border: 1.5px solid var(--svc-line);
  border-radius: 10px;
  box-shadow: 0 8px 22px var(--svc-12);
  padding: 12px;
}
i {
  display: block;
  height: 7px;
  border-radius: 4px;
  background: var(--svc-12);
  margin: 7px 0;
}
.w70 { width: 70%; } .w60 { width: 60%; } .w50 { width: 50%; }
.short { width: 45%; }

/* scene: standardized */
.svc-seal {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid var(--svc);
  background: var(--svc-soft);
  color: var(--svc);
  font-weight: 800;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  animation: svc-stamp 2.6s ease-in-out infinite;
  &.sm {
    width: 22px;
    height: 22px;
    font-size: 0.72rem;
    border-width: 2px;
    margin: 0;
    animation: none;
  }
}
.svc-gauge {
  position: relative;
  width: 80px;
  height: 44px;
  border: 3px solid var(--svc-line);
  border-bottom: none;
  border-top-left-radius: 80px;
  border-top-right-radius: 80px;
  background: #fff;
}
.svc-gauge-dot {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  margin: 0;
  border-radius: 50%;
  background: var(--svc);
}
.svc-needle {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 3px;
  height: 34px;
  border-radius: 2px;
  background: var(--svc);
  transform-origin: bottom center;
  animation: svc-needle 2.6s ease-in-out infinite;
}
@keyframes svc-stamp {
  0%, 100% { transform: scale(1); }
  45% { transform: scale(1.08); }
}
@keyframes svc-needle {
  0%, 100% { transform: translateX(-50%) rotate(-68deg); }
  50% { transform: translateX(-50%) rotate(60deg); }
}

/* scene: online-test (formulario respondido y corregido) */
.svc-device {
  position: relative;
  width: 66%;
  background: #fff;
  border: 2.5px solid var(--svc-line);
  border-radius: 12px;
  padding: 12px 10px 26px;
  box-shadow: 0 8px 22px var(--svc-12);
}
.svc-dev-q {
  display: block;
  height: 8px;
  width: 80%;
  border-radius: 4px;
  background: var(--svc-2);
  margin-bottom: 10px;
}
.svc-opt {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1.5px solid var(--svc-line);
  border-radius: 7px;
  padding: 5px 7px;
  margin: 6px 0;
  i { flex: 1; margin: 0; height: 6px; }
}
.svc-opt-dot {
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  border-radius: 50%;
  border: 2px solid var(--svc-line);
  animation: svc-answer 3.4s ease-in-out infinite;
  animation-delay: var(--d);
}
.svc-opt-check {
  flex: 0 0 auto;
  color: #2bb673;
  font-weight: 800;
  font-size: 12px;
  opacity: 0;
  animation: svc-grade 3.4s ease-in-out infinite;
  animation-delay: calc(var(--d) + 0.9s);
}
.svc-dev-grade {
  position: absolute;
  right: 8px;
  bottom: 6px;
  background: var(--svc);
  color: #fff;
  font-size: 0.55rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  opacity: 0;
  animation: svc-gradepop 3.4s ease-in-out infinite;
}
@keyframes svc-answer {
  0%, 8% { background: transparent; border-color: var(--svc-line); }
  20%, 100% { background: var(--svc); border-color: var(--svc); }
}
@keyframes svc-grade {
  0%, 22% { opacity: 0; transform: scale(0.4); }
  34%, 100% { opacity: 1; transform: scale(1); }
}
@keyframes svc-gradepop {
  0%, 62% { opacity: 0; transform: scale(0.6); }
  74%, 100% { opacity: 1; transform: scale(1); }
}

/* scene: report */
.svc-report { width: 80%; }
.svc-rep-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.svc-bars {
  display: flex;
  align-items: flex-end;
  gap: 7px;
  height: 74px;
  span {
    flex: 1;
    height: 0;
    border-radius: 4px 4px 0 0;
    background: linear-gradient(180deg, var(--svc) 0%, var(--svc-2) 100%);
    animation: svc-bar 2.8s ease-in-out infinite;
    animation-delay: var(--d);
  }
}
@keyframes svc-bar {
  0% { height: 0; }
  45%, 80% { height: var(--h); }
  100% { height: 0; }
}

/* scene: dua */
.svc-dua {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-dua-doc { width: 92px; }
.svc-kid {
  position: absolute;
  font-size: 22px;
  animation: svc-bob 4s ease-in-out infinite;
  &.k1 { top: 8px; left: 28%; }
  &.k2 { bottom: 10px; left: 22%; animation-delay: -1.3s; }
  &.k3 { top: 30%; right: 16%; animation-delay: -2.6s; }
}
@keyframes svc-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* scene: seconds */
.svc-watch {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 3px solid var(--svc);
  background: #fff;
  margin-right: 14px;
  &::before {
    content: "";
    position: absolute;
    top: -7px;
    left: 50%;
    transform: translateX(-50%);
    width: 10px;
    height: 5px;
    border-radius: 2px;
    background: var(--svc);
  }
}
.svc-watch-hand {
  position: absolute;
  bottom: 50%;
  left: 50%;
  width: 3px;
  height: 20px;
  border-radius: 2px;
  background: var(--svc);
  transform-origin: bottom center;
  animation: svc-spin 1.4s linear infinite;
}
@keyframes svc-spin {
  from { transform: translateX(-50%) rotate(0); }
  to { transform: translateX(-50%) rotate(360deg); }
}
.svc-quickdoc { position: relative; width: 80px; }
.svc-ready {
  position: absolute;
  right: -8px;
  bottom: -6px;
  background: var(--svc);
  color: #fff;
  font-size: 0.55rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  animation: svc-pop 2.4s ease-in-out infinite;
}
@keyframes svc-pop {
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.1); }
}

/* scene: download */
.svc-dl {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.svc-dl-doc { position: relative; width: 78px; margin-bottom: 26px; }
.svc-dl-arrow {
  position: absolute;
  left: 50%;
  bottom: -26px;
  transform: translateX(-50%);
  color: var(--svc);
  font-size: 18px;
  animation: svc-drop 1.8s ease-in-out infinite;
}
.svc-laptop {
  position: relative;
  width: 90px;
  height: 10px;
  border-radius: 3px;
  background: var(--svc-2);
  &::before {
    content: "";
    position: absolute;
    bottom: 10px;
    left: 14px;
    right: 14px;
    height: 28px;
    border: 3px solid var(--svc-2);
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    background: #fff;
  }
}
@keyframes svc-drop {
  0% { transform: translate(-50%, -8px); opacity: 0; }
  40% { opacity: 1; }
  100% { transform: translate(-50%, 8px); opacity: 0; }
}

/* scene: oa-select (varios OA + indicadores) */
.svc-oalist { width: 82%; }
.svc-oa-head {
  display: inline-block;
  margin-bottom: 8px;
  background: var(--svc-soft);
  color: var(--svc);
  font-size: 0.55rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
}
.svc-oa-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
  opacity: 0;
  animation: svc-rowfade 3.4s ease-in-out infinite;
  animation-delay: var(--d);
  i { margin: 0; }
  &.ind { padding-left: 16px; }
}
.svc-oa-box {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  border-radius: 4px;
  background: var(--svc);
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-oa-row.ind .svc-oa-box {
  width: 11px;
  height: 11px;
  flex: 0 0 11px;
  border-radius: 3px;
  font-size: 7px;
  background: var(--svc-2);
}

/* scene: details (detalles adicionales) */
.svc-details { width: 78%; }
.svc-det-label {
  display: inline-block;
  margin-bottom: 8px;
  background: var(--svc-soft);
  color: var(--svc);
  font-size: 0.55rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
}

/* scene: powerpoint (planificación -> presentación) */
.svc-ppt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}
.svc-ppt-doc { width: 64px; padding: 9px 8px; }
.svc-ppt-arrow {
  color: var(--svc);
  font-size: 16px;
  animation: svc-feedx 2.4s ease-in-out infinite;
}
.svc-slides {
  position: relative;
  width: 72px;
  height: 92px;
}
.svc-slide {
  position: absolute;
  left: 0;
  right: 0;
  height: 24px;
  border-radius: 6px;
  background: #fff;
  border: 1.5px solid var(--svc-line);
  box-shadow: 0 5px 12px var(--svc-12);
  opacity: 0;
  animation: svc-slidein 3s ease-in-out infinite;
  animation-delay: var(--d);
  &:nth-child(1) { top: 0; }
  &:nth-child(2) { top: 32px; }
  &:nth-child(3) { top: 64px; }
}
.svc-ppt-tag {
  position: absolute;
  top: -8px;
  left: 6px;
  background: #d24726;
  color: #fff;
  font-size: 0.52rem;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 5px;
}
@keyframes svc-feedx {
  0%, 100% { transform: translateX(0); opacity: 0.6; }
  50% { transform: translateX(3px); opacity: 1; }
}
@keyframes svc-slidein {
  0% { opacity: 0; transform: translateX(10px); }
  18%, 85% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(10px); }
}

/* scene: ai-generate */
.svc-aidoc { position: relative; width: 74%; }
.svc-wand {
  position: absolute;
  top: -10px;
  right: -6px;
  font-size: 20px;
  animation: svc-spark 1.8s ease-in-out infinite;
}
.svc-gline {
  height: 7px;
  width: 90%;
  margin: 8px 0;
  border-radius: 4px;
  background: var(--svc-2);
  transform-origin: left;
  transform: scaleX(0);
  animation: svc-grow 3s ease-in-out infinite;
  animation-delay: var(--d);
  &.short { width: 55%; }
}
@keyframes svc-grow {
  0% { transform: scaleX(0); opacity: 0; }
  10% { opacity: 1; }
  40%, 85% { transform: scaleX(1); opacity: 1; }
  100% { transform: scaleX(0); opacity: 0; }
}
@keyframes svc-spark {
  0%, 100% { transform: scale(1) rotate(0); opacity: 0.7; }
  50% { transform: scale(1.2) rotate(12deg); opacity: 1; }
}

/* scene: self-correct */
.svc-correct { position: relative; width: 72%; }
.svc-crow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  opacity: 0;
  animation: svc-rowfade 2.8s ease-in-out infinite;
  animation-delay: var(--d);
  i { flex: 1; margin: 0; }
}
.svc-tick { color: #2bb673; font-weight: 800; font-size: 13px; }
.svc-score {
  position: absolute;
  right: -8px;
  bottom: -10px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid var(--svc);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: svc-pop 2.6s ease-in-out infinite;
  b { font-size: 0.6rem; color: var(--svc); }
}
@keyframes svc-rowfade {
  0% { opacity: 0; transform: translateX(-8px); }
  20%, 82% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-8px); }
}

/* scene: catalog */
.svc-catalog {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 80%;
}
.svc-mcard {
  position: relative;
  height: 36px;
  border-radius: 7px;
  background: #fff;
  border: 1.5px solid var(--svc-line);
  box-shadow: 0 5px 12px var(--svc-12);
  opacity: 0;
  animation: svc-cardin 3s ease-in-out infinite;
  animation-delay: var(--d);
  &.premium { border-color: var(--svc); background: var(--svc-soft); }
}
.svc-star {
  position: absolute;
  top: -7px;
  right: -5px;
  color: var(--svc);
  font-size: 13px;
}
@keyframes svc-cardin {
  0% { opacity: 0; transform: scale(0.8); }
  18%, 85% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8); }
}

/* scene: checklist */
.svc-checklist { width: 82%; }
.svc-cli {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 7px 0;
  opacity: 0;
  animation: svc-rowfade 3s ease-in-out infinite;
  animation-delay: var(--d);
}
.svc-cbox {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border-radius: 4px;
  background: var(--svc);
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-cname { font-size: 0.62rem; color: $ink; }

/* scene: transfer */
.svc-transfer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.svc-tdoc { width: 64px; padding: 9px 8px; }
.svc-tfill {
  position: relative;
  height: 6px;
  margin: 5px 0;
  border-radius: 4px;
  background: var(--svc-12);
  overflow: hidden;
  &.short { width: 45%; }
  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0;
    border-radius: 4px;
    background: var(--svc-2);
    animation: svc-fill 3s ease-in-out infinite;
    animation-delay: var(--d);
  }
}
.svc-tlink {
  position: relative;
  width: 54px;
  height: 2px;
  margin: 0 6px;
  background: repeating-linear-gradient(
    90deg,
    var(--svc-2) 0 6px,
    transparent 6px 12px
  );
}
.svc-tpacket {
  position: absolute;
  top: -5px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: var(--svc);
  box-shadow: 0 0 9px var(--svc-glow);
  animation: svc-travel 3s ease-in-out infinite;
}
@keyframes svc-fill {
  0% { width: 0; }
  30%, 85% { width: 100%; }
  100% { width: 0; }
}
@keyframes svc-travel {
  0% { left: -5px; opacity: 0; transform: scale(0.6); }
  15% { opacity: 1; transform: scale(1); }
  85% { opacity: 1; transform: scale(1); }
  100% { left: calc(100% - 5px); opacity: 0; transform: scale(0.6); }
}

/* scene: suggestions */
.svc-sugdoc { position: relative; width: 74%; }
.svc-bulb {
  position: absolute;
  top: -10px;
  right: -4px;
  font-size: 20px;
  animation: svc-spark 1.8s ease-in-out infinite;
}
.svc-chips {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  span {
    font-size: 0.55rem;
    font-weight: 700;
    color: #fff;
    background: var(--svc);
    padding: 3px 8px;
    border-radius: 10px;
    opacity: 0;
    animation: svc-chipin 2.6s ease-in-out infinite;
    animation-delay: var(--d);
  }
}
@keyframes svc-chipin {
  0% { opacity: 0; transform: translateY(6px); }
  25%, 80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(6px); }
}

/* scene: scores-input */
.svc-scores { width: 74%; }
.svc-srow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 9px 0;
  i { flex: 1; margin: 0; }
}
.svc-num {
  flex: 0 0 auto;
  min-width: 26px;
  text-align: center;
  font-family: "JetBrains Mono", monospace;
  font-weight: 700;
  font-size: 0.7rem;
  color: var(--svc);
  background: var(--svc-soft);
  border: 1.5px solid var(--svc-line);
  border-radius: 6px;
  padding: 2px 6px;
  opacity: 0;
  animation: svc-numin 2.8s ease-in-out infinite;
  animation-delay: var(--d);
}
@keyframes svc-numin {
  0% { opacity: 0; transform: scale(0.6); }
  22%, 82% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.6); }
}

/* scene: word */
.svc-word { position: relative; display: flex; align-items: center; }
.svc-wfile {
  position: relative;
  width: 72px;
  background: #fff;
  border: 1.5px solid var(--svc-line);
  border-radius: 8px;
  padding: 16px 8px 8px;
  box-shadow: 0 8px 18px var(--svc-12);
  animation: svc-pop 2.8s ease-in-out infinite;
}
.svc-wmark {
  position: absolute;
  top: -9px;
  left: 8px;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #2b579a;
  color: #fff;
  font-weight: 800;
  font-size: 0.62rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.svc-wcheck {
  margin-left: 14px;
  color: #2bb673;
  font-size: 22px;
  font-weight: 800;
  animation: svc-pop 2.8s ease-in-out infinite;
}

/* mobile */
@media (max-width: 575px) {
  .svc-stage {
    flex-direction: column;
    gap: 12px;
    min-height: 300px;
  }
  .svc-scene-wrap {
    flex: 0 0 150px;
    max-width: 100%;
    width: 100%;
    height: 150px;
  }
  .svc-caption {
    text-align: center;
    .svc-icon { margin: 0 auto 8px; }
  }
}

/* reduced motion */
@media (prefers-reduced-motion: reduce) {
  .svc-scene *,
  .svc-needle,
  .svc-watch-hand {
    animation: none !important;
  }
  .svc-tfill::after,
  .svc-recvbar::after {
    animation: none !important;
    width: 100% !important;
  }
  .svc-gline { transform: scaleX(1); opacity: 1; }
  .svc-crow,
  .svc-cli,
  .svc-mcard,
  .svc-num,
  .svc-chips span,
  .svc-opt-check,
  .svc-dev-grade,
  .svc-oa-row,
  .svc-slide { opacity: 1; }
  .svc-opt-dot { background: var(--svc); border-color: var(--svc); }
  .svc-bars span { height: var(--h); }
}
</style>
